import {
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  type Theme,
} from "@mui/material";

import { Text } from "../../components/Text";

export interface NoShowFormContentProps {
  canRequestReplacement?: boolean;
  /** Label for the confirmation checkbox. Defaults to generic confirmation text. */
  confirmLabel?: string;
  hasReasonError?: boolean;
  isAlreadyRequestingReplacement?: boolean;
  isConfirmed: boolean;
  reasonDescription: string;
  replacementSelection: boolean | null;
  onConfirmedChange: (checked: boolean) => void;
  onReasonDescriptionChange: (value: string) => void;
  onReplacementSelectionChange: (value: boolean) => void;
}

/**
 * Shared form content for No Show (NCNS) reporting.
 * Used by both WorkerNoShowModal (legacy) and FcmNoShowDialog (redesign).
 */
export function NoShowFormContent(props: NoShowFormContentProps) {
  const {
    canRequestReplacement,
    confirmLabel = "I confirm this professional was a No Show.",
    hasReasonError,
    isAlreadyRequestingReplacement,
    isConfirmed,
    reasonDescription,
    replacementSelection,
    onConfirmedChange,
    onReasonDescriptionChange,
    onReplacementSelectionChange,
  } = props;

  return (
    <Box
      sx={(theme: Theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
      })}
    >
      <Text variant="body2">Please confirm that this professional was a No Show.</Text>

      <TextField
        fullWidth
        multiline
        // oxlint-disable-next-line typescript/no-explicit-any
        inputProps={{ "data-testid": "worker-no-show-modal-textbox" } as any}
        placeholder="Please add notes to describe the No Show event in more detail."
        rows={4}
        value={reasonDescription}
        error={hasReasonError}
        onChange={(event) => onReasonDescriptionChange(event.target.value)}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={isConfirmed}
            // oxlint-disable-next-line typescript/no-explicit-any
            inputProps={{ "data-testid": "agreement-checkbox" } as any}
            onChange={(event) => onConfirmedChange(event.target.checked)}
          />
        }
        label={confirmLabel}
        sx={(theme: Theme) => ({
          alignItems: "center",
          "& .MuiFormControlLabel-label": {
            fontSize: theme.typography.body2.fontSize,
            color: theme.palette.text.secondary,
          },
        })}
      />

      {canRequestReplacement === true && (
        <Box
          sx={(theme: Theme) => ({
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(2),
          })}
        >
          {isAlreadyRequestingReplacement === true ? (
            <Text variant="body2">
              We are already looking for a replacement worker for this shift. If you still want to
              cancel the shift, you&apos;ll be able to after this step.
            </Text>
          ) : (
            <>
              <Text
                variant="body2"
                sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}
              >
                Would you like Clipboard to look for a replacement for this shift?
              </Text>
              <RadioGroup
                value={replacementSelection === null ? "" : String(replacementSelection)}
                onChange={(event) => onReplacementSelectionChange(event.target.value === "true")}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label={
                    <>
                      Yes, I&apos;d like Clipboard to post an <strong>urgent shift</strong>. Note,
                      you won&apos;t be charged any additional fees, just the standard shift rate.
                    </>
                  }
                />
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="No, I don't need a replacement worker for this shift."
                />
              </RadioGroup>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
